import { RadioChangeEvent, Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { PermissionSetType, UserType } from 'api/models';
import { View } from 'api/views/types';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { Switch } from 'components/atoms/switch/switch';
import { toastService } from 'components/molecules/toast';
import {
  ButtonGroup,
  DrawerContent,
} from 'components/organisms/task-pane/task-detail';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { CancelSaveViewButton, SaveUpdateViewButton } from '../components';
import {
  Content,
  DrawerAtom,
  FormItemStyled,
  FormStyled,
} from '../manage-view-drawer';
import { CreatableView } from '../types';

const CustomerPortalText = styled.span<{ onlyMe: boolean }>`
  color: ${({ onlyMe }) => (onlyMe ? 'var(--gray-50)' : 'var(--gray-100)')};
`;

interface Props<ViewDefinition = any> {
  visible: boolean;
  currentViewDefinition: ViewDefinition;
  onClose: () => void;
  view?: View<ViewDefinition>;
  onViewUpdate?: (view: View<ViewDefinition>) => Promise<View<ViewDefinition>>;
  onViewCreate?: (
    view: CreatableView<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
}

export const ManageProjectViewDrawer: FC<Props> = ({
  visible,
  onClose,
  view,
  currentViewDefinition,
  onViewUpdate,
  onViewCreate,
}) => {
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [onlyMe, setOnlyMe] = useState(true);

  const editMode = view != null;
  const [form] = Form.useForm();
  const inputRef = useRef<any>(null);
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isCollaborator =
    currentUserPermissionSetType === PermissionSetType.Collaborator ||
    currentUserPermissionSetType ===
      PermissionSetType.AccountVendorCollaborator;
  const {
    user: { userId: currentUserId, userType: currentUserType },
  } = useUser();
  const translate = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();

  const onFormValueChange = useCallback((_, allValues) => {
    setSaveDisabled(isEmpty(allValues.viewName));
  }, []);

  const setFocus = useCallback(() => {
    if (visible) {
      inputRef.current.focus();
    }
    if (visible && inputRef.current && view?.viewName) {
      inputRef.current.setSelectionRange(
        view.viewName.length,
        view.viewName.length,
      );
      inputRef.current.focus();
    }
  }, [view?.viewName, visible]);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(
        view
          ? {
              ...view,
              inCustomerPortal:
                view.viewFilterShareConfig?.sharedWithCustomerPortal,
            }
          : {
              viewName: '',
              self: true,
              inCustomerPortal: false,
            },
      );
      setSaveDisabled(isEmpty(view?.viewName));
    } else {
      form.resetFields();
      setOnlyMe(true);
    }
  }, [visible, form, view]);

  useEffect(() => {
    if (view) {
      setOnlyMe(view.self);
    }
  }, [view]);

  const onViewEdit = useCallback(
    (formValues) => {
      onViewUpdate?.({
        ...view,
        ...formValues,
        viewDefinition: currentViewDefinition,
        viewSourceId: Number(projectId),
        viewFilterShareConfig: {
          sharedWithCustomerPortal: formValues.inCustomerPortal,
        },
      } as View<any>)
        .then(() => {
          toastService({
            title: translate('success'),
            message: translate('views.update.success', {
              viewName: formValues.viewName,
            }),
          }).success();
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('views.update.failed', {
              viewName: formValues.viewName,
            }),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    },
    [currentViewDefinition, onClose, onViewUpdate, projectId, translate, view],
  );

  const onViewNewCreate = useCallback(
    (formValues) => {
      onViewCreate?.({
        self: formValues.self,
        viewName: formValues.viewName,
        viewDefinition: currentViewDefinition,
        viewSourceId: Number(projectId),
        viewFilterShareConfig: {
          sharedWithCustomerPortal: formValues.inCustomerPortal ?? false,
        },
      })
        .then(() => {
          toastService({
            title: translate('success'),
            message: translate('views.create.success', {
              viewName: formValues.viewName,
            }),
          }).success();
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('views.create.failed', {
              viewName: formValues.viewName,
            }),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    },
    [currentViewDefinition, onClose, onViewCreate, projectId, translate],
  );

  const onSave = useCallback(() => {
    const formValues = form.getFieldsValue();

    setActionInProgress(true);
    if (editMode) {
      onViewEdit(formValues);
    } else {
      onViewNewCreate(formValues);
    }
  }, [form, editMode, onViewEdit, onViewNewCreate]);

  const viewAvailableForToggleHandler = useCallback(
    (e: RadioChangeEvent) => {
      if (e.target.value) {
        form.setFieldsValue({
          ...form.getFieldsValue(),
          inCustomerPortal: false,
        });
      }
      setOnlyMe(e.target.value);
    },
    [form],
  );

  return (
    <DrawerAtom
      visible={visible}
      onClose={onClose}
      destroyOnClose
      afterVisibleChange={setFocus}
    >
      <Content>
        <DrawerContent.Title data-cy="timesheets.task-drawer.add_tasks">
          {editMode ? (
            <FormattedMessage id="views.edit_view.title" />
          ) : (
            <FormattedMessage id="views.new_view.title" />
          )}
        </DrawerContent.Title>
        <DrawerContent.Content>
          <FormStyled
            layout="vertical"
            onValuesChange={onFormValueChange}
            form={form}
          >
            <Form.Item
              label={<FormattedMessage id="views.view_name.label" />}
              name="viewName"
            >
              <Input
                data-cy="views.new-view.name"
                placeholder={useTranslation('views.new_view_name.placeholder')}
                autoFocus
                autoComplete="off"
                ref={inputRef}
              />
            </Form.Item>

            <Form.Item
              label={<FormattedMessage id="views.view_available.label" />}
              name="self"
            >
              <RadioGroup onChange={(e) => viewAvailableForToggleHandler(e)}>
                <Space direction="vertical">
                  <Radio
                    data-cy="views.new-view.available.options.me"
                    value
                    disabled={
                      isCollaborator ||
                      (editMode && view.createdBy !== currentUserId)
                    }
                  >
                    <FormattedMessage id="views.view_available.options.me" />
                  </Radio>
                  <Radio
                    data-cy="views.new-view.available.options.team"
                    value={false}
                    disabled={
                      isCollaborator ||
                      (editMode && view.createdBy !== currentUserId)
                    }
                  >
                    <FormattedMessage id="views.view_available.options.team" />
                  </Radio>
                </Space>
              </RadioGroup>
            </Form.Item>

            <Space align="center">
              <FormItemStyled
                name="inCustomerPortal"
                valuePropName="checked"
                hidden={currentUserType === UserType.Partner}
              >
                <Switch disabled={onlyMe} size="small" />
              </FormItemStyled>
              {currentUserType !== UserType.Partner ? (
                <CustomerPortalText onlyMe={onlyMe}>
                  <FormattedMessage id="views.project.customer.portal" />
                </CustomerPortalText>
              ) : null}
            </Space>
          </FormStyled>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <ButtonGroup $reversed>
            <SaveUpdateViewButton
              disabled={saveDisabled}
              onClick={onSave}
              loading={actionInProgress}
            >
              {editMode ? (
                <FormattedMessage id="update" />
              ) : (
                <FormattedMessage id="save" />
              )}
            </SaveUpdateViewButton>
            <CancelSaveViewButton onClick={onClose} />
          </ButtonGroup>
        </DrawerContent.Footer>
      </Content>
    </DrawerAtom>
  );
};
